import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { PlantDTO } from '../DTOs/Plants/PlantDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { StoppageSubTypeDto } from '../DTOs/Stoppage/StoppageSubTypeDto'
import { StoppageTypeDto } from '../DTOs/Stoppage/StoppageTypeDto'
import { queryClient } from '../queryClient'
import type { TripleShift } from '../types/Shift'
import {
  appendCachedPlantStoppage,
  appendCachedStoppage,
  patchCachedOperationStatus,
} from './patchCachedStoppages'
import { stoppagesMutations } from './stoppagesMutations'

export const PLANT_JOB_TYPES_KEY = ['references', 'plantJobTypes'] as const

interface SubTypeOption {
  value: number
  label: string
  groupColor: string
  stoppageType: string
}

interface OfflineBatch {
  machineIds: number[]
  /** The machines as the modal listed them, so each row can carry its own. */
  machines?: PlantDTO[]
  stoppageTypeId: number
  stoppageSubTypeId: number
  timeIn: DateTime
  timeOut?: DateTime
  subType?: SubTypeOption
  selectedDate: DateTime
  selectedShift: TripleShift
}

// Note: the plants list the prefetch warms, which carries each machine's job types as a bitmask.
function cachedPlants(): PlantDTO[] {
  return queryClient.getQueryData<PlantDTO[]>(['references', 'plantsWithDepartments']) ?? []
}

// Note: offline stand-in for the multiple-stoppage-plants endpoint, which works out server-side which
// machines are free in the chosen window. The cached operations already say which machines are up, so
// use those. A job-type filter is applied from the cached plants list - it used to be ignored offline,
// so choosing a job type appeared to do nothing at all.
export function availableMachinesFromCache(
  selectedDate: DateTime,
  selectedShift: TripleShift,
  jobTypes?: number,
): PlantDTO[] {
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift],
  })) {
    const rows = page?.data ?? []
    if (!rows.length) continue

    // Note: the plant record is preferred where the cache has one, because it carries the job types
    // and the department; the operation row alone gives the id and the name.
    const plants = cachedPlants()

    return rows
      .filter(row => row.status === 'UP' && row.plantId)
      .map(row => plants.find(plant => plant.id === row.plantId)
        ?? ({ id: row.plantId, plantRef: row.machineName } as PlantDTO))
      // Note: a machine whose job types we do not know is kept rather than hidden - offline the list
      // erring towards showing a machine is safer than silently omitting one the crew expects.
      .filter(plant => !jobTypes || plant.jobTypes == null || (plant.jobTypes & jobTypes) !== 0)
  }
  return []
}

// Note: the batch endpoint has nothing to queue against offline, so the batch becomes one ordinary
// queued create per machine. Each one patches the same caches a single offline stoppage does, so the
// Operations list shows the machines as down and the Stoppages list shows the new records.
export async function addMultipleStoppagesOffline(batch: OfflineBatch): Promise<number> {
  const dateIso = batch.selectedDate.toISODate()!
  let added = 0

  for (const plantId of batch.machineIds) {
    const response = await stoppagesMutations.addStoppage(new StoppageDto({
      plantId,
      startTime: batch.timeIn,
      endTime: batch.timeOut,
      stoppageTypeId: batch.stoppageTypeId,
      stoppageSubTypeId: batch.stoppageSubTypeId,
      incident: false,
    }))

    if (!response.data) continue

    const row = new StoppageDto({
      ...response.data,
      // Note: carry the machine with the row rather than resolving it from the caches later.
      plant: (batch.machines?.find(machine => machine.id === plantId)
        ?? { id: plantId } as PlantDTO) as StoppageDto['plant'],
      stoppageSubType: new StoppageSubTypeDto({
        stoppageSubTypeId: batch.stoppageSubTypeId,
        stoppageSubType: batch.subType?.label ?? '',
        stoppageTypeId: batch.stoppageTypeId,
        departmentOwnerId: 0,
      }),
      stoppageType: new StoppageTypeDto({
        stoppageTypeId: batch.stoppageTypeId,
        stoppageType: batch.subType?.stoppageType ?? '',
        colourHex: batch.subType?.groupColor,
      }),
    })

    appendCachedStoppage(row, dateIso, batch.selectedShift)
    appendCachedPlantStoppage(row, dateIso, batch.selectedShift, plantId)

    // Note: only a stoppage that is still open takes the machine down; one entered with a time out
    // (a meal break already finished, say) leaves it up.
    if (!batch.timeOut) {
      patchCachedOperationStatus(plantId, dateIso, batch.selectedShift, {
        status: batch.subType?.stoppageType ?? 'Maintenance Down',
        statusDescription: `${batch.subType?.stoppageType ?? ''} - ${batch.subType?.label ?? ''}`.trim(),
        stoppageColourHub: batch.subType?.groupColor,
        stoppageId: response.data.stoppageId,
        currentStoppageTypeId: batch.stoppageTypeId,
        currentStoppageSubTypeId: batch.stoppageSubTypeId,
        timeIn: batch.timeIn,
      })
    }

    added++
  }

  return added
}
